import type { Router } from 'vue-router'
import type {
	AppNotification,
	NotificationDto,
	NotificationKind,
	NotificationTarget,
} from '../types/notification-center.types'

// The backend's links point at the Vue 2 pages (WebAppBaseUrl + /v2/...).
const BULK_DOWNLOAD_PATH = /^\/v2\/bulkdownload\//i
const CONTACT_LIST_PATH = /^\/v2\/userlistdetail\//i
const COMPANY_LIST_PATH = /^\/v2\/userlistcompanydetail\//i
const LEAD_ENRICHMENT_PATH = /^\/v2\/leadenrichment\b/i
const LEAD_SCORING_PATH = /^\/v2\/leadscoringlist\b/i

function linkPath(link: string | null): string {
	if (!link) return ''
	try {
		return new URL(link, window.location.origin).pathname
	} catch {
		return ''
	}
}

/**
 * Which job a notification reports on. The wording is checked first: a lead enrichment and a list
 * refresh both link to the contact list, and a lead scoring and a lead enrichment both link to the
 * company list.
 */
export function notificationKind(message: string, link: string | null): NotificationKind {
	if (/lead scor/i.test(message)) return 'lead-scoring'
	if (/lead enrichment/i.test(message)) return 'lead-enrichment'
	if (/contact and email refresh/i.test(message)) return 'contact-refresh'

	const path = linkPath(link)
	if (BULK_DOWNLOAD_PATH.test(path) || /bulk download/i.test(message)) return 'bulk-download'
	if (CONTACT_LIST_PATH.test(path) || /refresh/i.test(message)) return 'list-refresh'
	if (LEAD_ENRICHMENT_PATH.test(path) || COMPANY_LIST_PATH.test(path)) return 'lead-enrichment'
	if (LEAD_SCORING_PATH.test(path)) return 'lead-scoring'
	return 'general'
}

// The backend wrote "lead scoing" until 2026-10-21; stored notifications keep the typo.
function tidyMessage(message: string): string {
	return message.replace(/\blead scoing\b/gi, 'lead scoring').trim()
}

export function toAppNotification(dto: NotificationDto): AppNotification {
	const message = tidyMessage(dto.Message ?? '')
	const link = dto.Link || null
	return {
		id: dto.Id,
		createdAt: dto.CreatedAt,
		message,
		link,
		unread: dto.State === 'unread',
		kind: notificationKind(message, link),
		failed: /^there was a problem/i.test(message),
	}
}

/**
 * Where a notification leads. A link to this app (same origin, or a /v2 path, which the router
 * maps to its Vue 3 page) opens in place; anything else opens in a new tab, as in Vue 2. Only http
 * and https links are followed.
 */
export function notificationTarget(link: string | null, router: Router): NotificationTarget | null {
	if (!link) return null

	let url: URL
	try {
		url = new URL(link, window.location.origin)
	} catch {
		return null
	}
	if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

	const sameApp = url.origin === window.location.origin || /^\/v2\//i.test(url.pathname)
	if (sameApp) {
		const path = `${url.pathname}${url.search}${url.hash}`
		const resolved = router.resolve(path)
		const known = resolved.matched.length > 0 && !resolved.matched.some((r) => r.name === 'NotFound')
		if (known) return { to: path }
	}
	return { href: url.href }
}
